import styled from "styled-components";
import { color, layer, media } from "../../styles/theme";

/**
 * Fractal noise rendered by the browser rather than shipped as an image —
 * no extra request, and it scales to any viewport.
 */
const GRAIN_SVG = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="220" height="220">
    <filter id="n">
      <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="4" stitchTiles="stitch"/>
      <feColorMatrix type="saturate" values="0"/>
    </filter>
    <rect width="100%" height="100%" filter="url(#n)" opacity="0.55"/>
  </svg>`.replace(/\s+/g, " ")
);

export const AtmosphereRoot = styled("div")`
  position: fixed;
  inset: 0;
  z-index: ${layer.atmosphere};
  pointer-events: none;
  overflow: hidden;
`;

/** Drifting cold light. Three blobs at different speeds reads as depth. */
export const FogLayer = styled("div")<{ $variant: 1 | 2 | 3 }>`
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  will-change: transform;
  opacity: 0.5;

  ${({ $variant }) =>
    $variant === 1 &&
    `
      top: -22vh;
      left: -12vw;
      width: 62vw;
      height: 62vw;
      background: radial-gradient(circle, rgba(142, 22, 22, 0.16), transparent 68%);
      animation: ghost-drift 42s ease-in-out infinite;
    `}

  ${({ $variant }) =>
    $variant === 2 &&
    `
      bottom: -28vh;
      right: -16vw;
      width: 70vw;
      height: 70vw;
      background: radial-gradient(circle, rgba(46, 54, 78, 0.34), transparent 66%);
      animation: ghost-drift 61s ease-in-out infinite reverse;
    `}

  ${({ $variant }) =>
    $variant === 3 &&
    `
      top: 34vh;
      left: 44vw;
      width: 44vw;
      height: 44vw;
      background: radial-gradient(circle, rgba(226, 89, 60, 0.07), transparent 70%);
      animation: ghost-drift 78s ease-in-out infinite;
    `}

  ${media.md} {
    filter: blur(60px);
    opacity: 0.42;
  }
`;

/** Darkens the corners so the eye is pulled to the middle of the page. */
export const Vignette = styled("div")`
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse 100% 85% at 50% 45%,
    transparent 40%,
    rgba(5, 5, 6, 0.55) 78%,
    ${color.void} 100%
  );
`;

export const Grain = styled("div")<{ $animated: boolean }>`
  position: absolute;
  /* Oversized so the animation can slide it without exposing an edge. */
  top: -60%;
  left: -60%;
  width: 220%;
  height: 220%;
  background-image: url("data:image/svg+xml,${GRAIN_SVG}");
  opacity: 0.05;
  mix-blend-mode: overlay;

  ${({ $animated }) =>
    $animated &&
    `animation: ghost-grain 900ms steps(4, end) infinite;`}

  ${media.md} {
    opacity: 0.035;
  }
`;

/** Faint horizontal banding — a dead CRT left on in the corner of the room. */
export const Scanlines = styled("div")`
  position: absolute;
  inset: 0;
  opacity: 0.5;
  background-image: repeating-linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.16) 0px,
    rgba(0, 0, 0, 0.16) 1px,
    transparent 1px,
    transparent 3px
  );
`;
